// MF-67 — Rich read-only view of a journal entry: severity/type/status/points badges, a facts grid,
// the template fields, the students involved, and the case timeline (status changes stand out from
// plain notes; follow-ups can be deleted). Rendered inside PreviewDialog.
import { useEffect, useState } from 'react'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { isImage } from './FileUpload'
import IconTooltipButton from '../../../button/IconTooltipButton'
import ConfirmDelete from './ConfirmDelete'
import ColorBadge, { CodeTag, Points, TypeBadge, StatusBadge } from './ColorBadge'
import { Fact, Facts, PreviewSection } from './PreviewDialog'
import { STATUS_COLORS } from './colorTokens'
import { useCategories, useSeveritiesMap, useTypes } from './useLookups'
import { deleteEntryEvent } from '../../../../services/journal/entries.service'

const EVENT_LABEL = {
  created: 'Opened',
  updated: 'Updated',
  note: 'Note',
  'status-changed': 'Status changed'
}
const EVENT_DOT = {
  created: '#2563EB',
  updated: '#94A3B8',
  note: '#64748B',
  'status-changed': STATUS_COLORS.actioned
}
const SOURCE_LABEL = { schoolbox: 'Schoolbox' }
const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null

const isFile = (v) => v && typeof v === 'object' && v.url

// Render a stored answer by its shape: files → thumbnails/links, lists → chips-like text, booleans → Yes/No.
const FileList = ({ files }) => {
  const images = files.filter(isImage)
  const others = files.filter((f) => !isImage(f))
  return (
    <div className='flex flex-col' style={{ gap: 8 }}>
      {images.length ? (
        <div className='flex flex-wrap' style={{ gap: 8 }}>
          {images.map((f) => (
            <a key={f.id || f.url} href={f.url} target='_blank' rel='noreferrer' title={f.name}>
              <img
                src={f.url}
                alt={f.name}
                style={{
                  width: 96,
                  height: 96,
                  objectFit: 'cover',
                  borderRadius: 8,
                  border: '1px solid rgb(226, 232, 240)'
                }}
              />
            </a>
          ))}
        </div>
      ) : null}
      {others.map((f) => (
        <a
          key={f.id || f.url}
          href={f.url}
          target='_blank'
          rel='noreferrer'
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            color: 'rgb(37, 99, 235)'
          }}
        >
          <FontAwesomeIcon icon={['fal', 'file-alt']} />
          {f.name}
        </a>
      ))}
    </div>
  )
}

const FieldValue = ({ value }) => {
  if (Array.isArray(value)) {
    if (value.some(isFile)) return <FileList files={value.filter(isFile)} />
    return value.join(', ')
  }
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  return value
}

const EntryPreview = ({ entry: entryProp = {}, onChange }) => {
  const severities = useSeveritiesMap()
  const { byCode: categories } = useCategories()
  const { byCode: typesByCode } = useTypes()
  const [entry, setEntry] = useState(entryProp)
  const [toDelete, setToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    setEntry(entryProp)
  }, [entryProp])

  // Keep the original index (delete is by position in events[]) before sorting for display.
  const events = (entry.events || [])
    .map((e, originalIndex) => ({ ...e, originalIndex }))
    .sort((a, b) => new Date(a.at || 0) - new Date(b.at || 0))

  // Only follow-ups (notes / status changes) can be removed — not the created/updated system events.
  const canDelete = (e) => e.type === 'note' || e.type === 'status-changed'

  const onConfirmDelete = async () => {
    setDeleting(true)
    try {
      const res = await deleteEntryEvent(entry._id, toDelete.originalIndex)
      if (res?.data) {
        setEntry(res.data)
        if (onChange) onChange(res.data)
      }
      setToDelete(null)
    } finally {
      setDeleting(false)
    }
  }

  const filledFields = (entry.fieldValues || []).filter(
    (f) =>
      !(
        f.value === null ||
        f.value === undefined ||
        f.value === '' ||
        (Array.isArray(f.value) && !f.value.length)
      )
  )
  const severityApplies = Boolean(typesByCode.get(entry.type)?.usesSeverity)
  const severity = severityApplies ? severities.get(entry.severity) : null
  const category = categories.get(entry.categoryCode)

  return (
    <div className='flex flex-col' style={{ gap: 16, minWidth: 0 }}>
      <div className='flex items-center flex-wrap' style={{ gap: 8 }}>
        <TypeBadge code={entry.type || 'neutral'} size='md' />
        {severity ? (
          <ColorBadge label={severity.label} color={severity.color} />
        ) : severityApplies && entry.severity ? (
          <CodeTag code={entry.severity} />
        ) : (
          <span style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>
            {severityApplies ? 'Severity: not set' : 'Severity: not applicable'}
          </span>
        )}
        <StatusBadge status={entry.status || 'open'} size='md' />
        <span style={{ fontSize: 13, marginLeft: 4 }}>
          <Points value={entry.points ?? 0} /> pts
        </span>
      </div>

      <Facts>
        <Fact label='Template' value={entry.template?.title} />
        <Fact label='Category' value={category?.name || entry.categoryCode} />
        <Fact label='Occurred' value={fmtDateTime(entry.occurredAt)} />
        <Fact label='Location' value={entry.location} />
        <Fact
          label='Reported by'
          value={(entry.reporters || [])
            .map((r) => r.name)
            .filter(Boolean)
            .join(', ')}
        />
        <Fact label='Recorded by' value={entry.recordedBy?.fullName} />
        {entry.source?.system ? (
          <Fact
            label='Source'
            full
            value={`Imported from ${SOURCE_LABEL[entry.source.system] || entry.source.system}${
              entry.source.externalId ? ` (record ${entry.source.externalId})` : ''
            }`}
          />
        ) : null}
      </Facts>

      {filledFields.length ? (
        <PreviewSection title='Details'>
          <Facts>
            {filledFields.map((f, i) => (
              <Fact key={f.fieldId || i} label={f.label} full value={<FieldValue value={f.value} />} />
            ))}
          </Facts>
        </PreviewSection>
      ) : null}

      {Array.isArray(entry.attachments) && entry.attachments.some(isFile) ? (
        <PreviewSection title={`Attachments (${entry.attachments.filter(isFile).length})`}>
          <FileList files={entry.attachments.filter(isFile)} />
        </PreviewSection>
      ) : null}

      {entry.students?.length ? (
        <PreviewSection title={`Students (${entry.students.length})`}>
          <div className='flex flex-col' style={{ gap: 6 }}>
            {entry.students.map((s, i) => (
              <div
                key={s.userId || i}
                className='flex items-center justify-between'
                style={{ gap: 12, padding: '8px 12px', borderRadius: 8, background: 'rgb(248, 250, 252)' }}
              >
                <span style={{ fontSize: 13, fontWeight: 500 }}>
                  {s.name}
                  {s.code ? (
                    <span style={{ color: 'rgb(100, 116, 139)', fontWeight: 400 }}> · {s.code}</span>
                  ) : null}
                </span>
                {s.role ? (
                  <span style={{ fontSize: 12, color: 'rgb(100, 116, 139)', textTransform: 'capitalize' }}>
                    {s.role}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </PreviewSection>
      ) : null}

      {events.length ? (
        <PreviewSection title='Case timeline'>
          <div className='flex flex-col'>
            {events.map((e, i) => (
              <div key={i} className='flex' style={{ gap: 12 }}>
                <div className='flex flex-col items-center'>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: EVENT_DOT[e.type] || '#94A3B8',
                      marginTop: 5,
                      flex: 'none'
                    }}
                  />
                  {i < events.length - 1 ? (
                    <span style={{ width: 2, flex: 1, background: 'rgb(226, 232, 240)', marginTop: 2 }} />
                  ) : null}
                </div>
                <div className='flex flex-col grow' style={{ gap: 4, paddingBottom: 14, minWidth: 0 }}>
                  <div className='flex items-center flex-wrap' style={{ gap: 8 }}>
                    <Typography style={{ fontSize: 13, fontWeight: 600, color: 'rgb(15, 23, 42)' }}>
                      {EVENT_LABEL[e.type] || e.type}
                    </Typography>
                    {e.status ? <StatusBadge status={e.status} /> : null}
                    <Typography style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>
                      {fmtDateTime(e.at)}
                      {e.by?.name ? ` · ${e.by.name}` : ''}
                    </Typography>
                    {canDelete(e) ? (
                      <span style={{ marginLeft: 'auto' }}>
                        <IconTooltipButton
                          title='Delete event'
                          icon={['fal', 'trash-alt']}
                          onClick={() =>
                            setToDelete({
                              originalIndex: e.originalIndex,
                              label: EVENT_LABEL[e.type] || e.type
                            })
                          }
                        />
                      </span>
                    ) : null}
                  </div>
                  {e.note ? (
                    <Typography style={{ fontSize: 13, color: 'rgb(51, 65, 85)', whiteSpace: 'pre-line' }}>
                      {e.note}
                    </Typography>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </PreviewSection>
      ) : null}

      <ConfirmDelete
        open={Boolean(toDelete)}
        entity='timeline event'
        name={toDelete?.label}
        loading={deleting}
        onCancel={() => setToDelete(null)}
        onConfirm={onConfirmDelete}
      />
    </div>
  )
}

export default EntryPreview
